"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import type {
  AccountLookupOutput,
  AddressSearchOutput,
  QuickPayOutput,
  QuickPayReceiptContext,
} from "@tillix/api-contract/client-schemas";
import { useT } from "@tillix/i18n/react";
import { api } from "@tillix/tx-core/orpc/client";
import { Button } from "@tillix/ui-atoms/button";
import { Card } from "@tillix/ui-atoms/card";
import {
  FieldControl,
  FieldError,
  FieldLabel,
  FieldRoot,
} from "@tillix/ui-atoms/field";
import { Form } from "@tillix/ui-atoms/form";
import { FormRootError } from "@tillix/ui-atoms/form-root-error";
import {
  Tabs,
  TabsList,
  TabsPanel,
  TabsTab,
} from "@tillix/ui-atoms/framing/tabs";
import { Input } from "@tillix/ui-atoms/input";
import { Hash, Lock, Receipt, Timer, UserRoundX } from "lucide-react";
import { observer } from "mobx-react-lite";
import { authClient } from "@/services/auth/client";
import AccountLookupResults from "./AccountLookupResults";
import AddressSearchForm from "./AddressSearchForm";
import QuickPayConfirmation from "./QuickPayConfirmation";
import QuickPayForm from "./QuickPayForm";
import { extractAccountFields } from "./utils/extract-account-fields";

type Step = "lookup" | "pay" | "confirmation";

function ContractAccountField({
  value,
  onChange,
  required,
  validationError,
  t,
}: {
  value: string;
  onChange: (value: string) => void;
  required?: boolean;
  validationError?: string;
  t: (key: string, options?: Record<string, unknown>) => string;
}) {
  return (
    <FieldRoot name="externalId">
      <FieldLabel>
        {t("quickPay.contractAccountNumber")}
        {required && <span className="text-destructive"> *</span>}
      </FieldLabel>
      <FieldControl
        render={(defaultProps) => (
          <div className="relative">
            <Hash
              aria-hidden="true"
              className="pointer-events-none absolute top-1/2 left-3 z-10 -translate-y-1/2 text-muted-foreground/72"
              size={18}
            />
            <Input
              {...defaultProps}
              className="ps-10"
              inputMode="numeric"
              maxLength={12}
              onChange={(e) => onChange(e.target.value)}
              placeholder={t("quickPay.enterContractAccountNumber")}
              value={value}
            />
          </div>
        )}
      />
      {validationError && <FieldError>{validationError}</FieldError>}
    </FieldRoot>
  );
}

const SUMMARY_TIMEOUT_MS = 5 * 60 * 1000;

interface AccountLookupProps {
  initialAccountNumber?: string;
  tenantId: string;
}

function AccountLookup({ initialAccountNumber, tenantId }: AccountLookupProps) {
  const { t } = useT();
  const [result, setResult] = useState<AccountLookupOutput | null>(null);
  const [step, setStep] = useState<Step>("lookup");
  const [paymentAmount, setPaymentAmount] = useState(0);
  const [paymentResult, setPaymentResult] = useState<QuickPayOutput | null>(
    null,
  );
  const [receiptContext, setReceiptContext] =
    useState<QuickPayReceiptContext | null>(null);
  const [addressResults, setAddressResults] = useState<
    AddressSearchOutput["results"] | null
  >(null);

  // Account number tab — two-step state
  const [externalId, setExternalId] = useState(initialAccountNumber ?? "");
  const [invoiceNumber, setInvoiceNumber] = useState("");
  const [isAccountValidated, setIsAccountValidated] = useState(false);
  const [isValidating, setIsValidating] = useState(false);
  const [isLookingUp, setIsLookingUp] = useState(false);
  const [accountError, setAccountError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState("account");
  const [addressFormKey, setAddressFormKey] = useState(0);

  const selectedAccountRef = useRef<{
    externalId: string;
    nameOnAccount?: string;
    zipCode?: string;
    invoiceNumber?: string;
  } | null>(null);

  const isExternalIdValid = externalId.trim().length === 12;
  const isInvoiceNumberValid = invoiceNumber.trim().length >= 1;

  const resetLookupState = useCallback(() => {
    setResult(null);
    setAddressResults(null);
    selectedAccountRef.current = null;
    setPaymentAmount(0);
    setExternalId("");
    setInvoiceNumber("");
    setIsAccountValidated(false);
    setAccountError(null);
    setAddressFormKey((k) => k + 1);
  }, []);

  useEffect(() => {
    const hasResults =
      result !== null || (addressResults !== null && addressResults.length > 0);
    if (!hasResults || step !== "lookup") return;

    const timer = setTimeout(() => {
      resetLookupState();
    }, SUMMARY_TIMEOUT_MS);

    return () => clearTimeout(timer);
  }, [result, addressResults, step, resetLookupState]);

  // Auto-validate account when arriving from a nudge notification link
  const autoValidateRef = useRef(false);
  useEffect(() => {
    if (autoValidateRef.current) return;
    if (!initialAccountNumber) return;

    autoValidateRef.current = true;
    setIsValidating(true);

    api.accountLookup
      .validate({ tenantId, externalId: initialAccountNumber.trim() })
      .then(() => {
        setIsAccountValidated(true);
      })
      .catch(() => {
        // Silent fallback — form field is already pre-filled
      })
      .finally(() => {
        setIsValidating(false);
      });
  }, [initialAccountNumber, tenantId]);

  const handleTabChange = (value: string | number | null) => {
    setActiveTab(String(value));
    resetLookupState();
  };

  // Step 1: Validate contract account exists
  const handleValidateAccount = async (e: React.FormEvent) => {
    e.preventDefault();
    setAccountError(null);
    setResult(null);
    setAddressResults(null);
    setIsValidating(true);
    try {
      await api.accountLookup.validate({
        tenantId,
        externalId: externalId.trim(),
      });
      setIsAccountValidated(true);
    } catch (err) {
      setAccountError(
        err instanceof Error ? err.message : t("quickPay.validationFailed"),
      );
    } finally {
      setIsValidating(false);
    }
  };

  // Step 2: Look up account by invoice
  const handleInvoiceLookup = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmedInvoiceNumber = invoiceNumber.trim();
    setAccountError(null);
    setResult(null);
    setAddressResults(null);
    setIsLookingUp(true);
    try {
      const details = await api.accountLookup.getByInvoice({
        tenantId,
        externalId: externalId.trim(),
        invoiceNumber: trimmedInvoiceNumber,
      });

      const accountHolder = details.accountHolder ?? "";
      const rawZip = (details.address?.zip ?? "")
        .replace(/[^0-9]/g, "")
        .slice(0, 5);

      selectedAccountRef.current = {
        externalId: externalId.trim(),
        ...(accountHolder && rawZip
          ? { nameOnAccount: accountHolder, zipCode: rawZip }
          : {}),
        invoiceNumber: trimmedInvoiceNumber,
      };
      setPaymentAmount(details.balance);
      // Clear inputs after results populate (PII hygiene)
      setExternalId("");
      setInvoiceNumber("");
      setIsAccountValidated(false);

      setResult(details);
    } catch (err) {
      setAccountError(
        err instanceof Error ? err.message : t("quickPay.lookupFailed"),
      );
    } finally {
      setIsLookingUp(false);
    }
  };

  // Reset account number tab when user changes the contract account
  const handleExternalIdChange = (value: string) => {
    setExternalId(value);
    if (isAccountValidated) {
      setIsAccountValidated(false);
      setInvoiceNumber("");
      setResult(null);
      setAddressResults(null);
      setAccountError(null);
    }
  };

  // Address tab — fetch account details directly using the contractAccount
  const handleAddressAccountSelected = useCallback(
    async (account: AddressSearchOutput["results"][number]) => {
      const {
        externalId: contractAccount,
        nameOnAccount,
        zipCode,
      } = extractAccountFields(account);
      try {
        const userAccountDetails = await api.accountLookup.get({
          tenantId,
          externalId: contractAccount,
          nameOnAccount,
          zipCode,
        });
        selectedAccountRef.current = {
          externalId: contractAccount,
          nameOnAccount,
          zipCode,
        };
        setAddressResults(null);
        setResult(userAccountDetails);
        setPaymentAmount(userAccountDetails.balance);
      } catch (err) {
        console.error(
          "Failed to fetch account details after address search",
          err,
        );
      }
    },
    [tenantId],
  );

  const handleSearchResults = useCallback(
    (results: AddressSearchOutput["results"]) => {
      setResult(null);
      // Clear address form inputs after results populate (PII hygiene)
      setAddressFormKey((k) => k + 1);

      setAddressResults(results);
    },
    [],
  );

  const handlePaymentSuccess = (
    output: QuickPayOutput,
    context: QuickPayReceiptContext,
  ) => {
    setPaymentResult(output);
    setReceiptContext(context);
    setStep("confirmation");
  };

  const handleDone = () => {
    resetLookupState();
    setPaymentResult(null);
    setReceiptContext(null);
    setStep("lookup");
  };

  const handleRegister = async (data: { email: string }) => {
    if (!selectedAccountRef.current) {
      throw new Error("No account selected");
    }

    // 1. Persist quick pay data in UserExpected
    const res = await fetch("/api/quick-pay/register", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        email: data.email,
        tenantId,
        externalId: selectedAccountRef.current.externalId,
        nameOnAccount: selectedAccountRef.current.nameOnAccount,
        zipCode: selectedAccountRef.current.zipCode,
      }),
    });

    if (!res.ok) {
      const body = await res
        .json()
        .catch(() => ({ error: "Registration failed" }));
      throw new Error(body.error ?? "Registration failed");
    }

    // 2. Send magic link from client (needs browser context for URL construction)
    const magicLinkResult = await authClient.signIn.magicLink({
      email: data.email,
      callbackURL: "/dashboard",
      newUserCallbackURL: "/onboarding",
    });

    if (magicLinkResult.error) {
      throw new Error(
        magicLinkResult.error.message ?? "Failed to send sign-in link",
      );
    }
  };

  if (step === "confirmation" && paymentResult && result) {
    return (
      <div className="my-auto flex w-full justify-center">
        <QuickPayConfirmation
          amount={paymentAmount}
          nameOnAccount={selectedAccountRef.current?.nameOnAccount ?? ""}
          onDone={handleDone}
          onRegister={handleRegister}
          receiptContext={receiptContext}
          result={paymentResult}
        />
      </div>
    );
  }

  if (step === "pay" && result && selectedAccountRef.current) {
    return (
      <div className="my-auto w-full max-w-4xl">
        <div className="grid gap-6 md:grid-cols-[minmax(0,1fr)_minmax(0,1.2fr)]">
          <AccountLookupResults result={result} />
          <QuickPayForm
            amount={paymentAmount}
            balance={result.balance}
            externalId={selectedAccountRef.current.externalId}
            invoiceNumber={selectedAccountRef.current.invoiceNumber}
            isCashOnly={result.cashOnly}
            nameOnAccount={selectedAccountRef.current.nameOnAccount}
            onAmountChange={setPaymentAmount}
            onCancel={() => setStep("lookup")}
            onSuccess={handlePaymentSuccess}
            tenantId={tenantId}
            zipCode={selectedAccountRef.current.zipCode}
          />
        </div>
      </div>
    );
  }

  const hasRightPanel =
    result !== null || (addressResults !== null && addressResults.length > 0);

  const formCard = (
    <Card>
      <Card.Header>
        <Card.Title>{t("quickPay.title")}</Card.Title>
        <Card.Description>{t("quickPay.makeInstantPayments")}</Card.Description>
      </Card.Header>

      <Card.Content>
        <Tabs onValueChange={handleTabChange} value={activeTab}>
          <TabsList className="mb-4 w-full">
            <TabsTab className="flex-1" value="account">
              {t("quickPay.accountNumber")}
            </TabsTab>
            <TabsTab className="flex-1" value="address">
              {t("quickPay.address")}
            </TabsTab>
          </TabsList>

          <TabsPanel value="account">
            {!isAccountValidated ? (
              <Form
                className="flex flex-col gap-0"
                onSubmit={handleValidateAccount}
              >
                <ContractAccountField
                  onChange={handleExternalIdChange}
                  required={true}
                  t={t}
                  validationError={
                    !isExternalIdValid && externalId.length > 0
                      ? t("quickPay.mustBe12Characters")
                      : undefined
                  }
                  value={externalId}
                />
                <FormRootError
                  keyPrefix="account-validate"
                  rootError={accountError ?? undefined}
                />
                <Button
                  className="mt-2 w-full"
                  disabled={!isExternalIdValid || isValidating}
                  type="submit"
                >
                  {isValidating
                    ? t("quickPay.validating")
                    : t("quickPay.validateAccount")}
                </Button>
              </Form>
            ) : (
              <Form
                className="flex flex-col gap-0"
                onSubmit={handleInvoiceLookup}
              >
                <ContractAccountField
                  onChange={handleExternalIdChange}
                  t={t}
                  value={externalId}
                />
                <p className="mb-1 text-success text-xs">
                  {t("quickPay.accountVerified")}
                </p>
                <FieldRoot name="invoiceNumber">
                  <FieldLabel>
                    {t("quickPay.invoiceNumber")}{" "}
                    <span className="text-destructive">*</span>
                  </FieldLabel>
                  <FieldControl
                    render={(defaultProps) => (
                      <div className="relative">
                        <Receipt
                          aria-hidden="true"
                          className="pointer-events-none absolute top-1/2 left-3 z-10 -translate-y-1/2 text-muted-foreground/72"
                          size={18}
                        />
                        <Input
                          {...defaultProps}
                          className="ps-10"
                          onChange={(e) => setInvoiceNumber(e.target.value)}
                          placeholder={t("quickPay.enterInvoiceNumber")}
                          value={invoiceNumber}
                        />
                      </div>
                    )}
                  />
                </FieldRoot>
                <FormRootError
                  keyPrefix="account-invoice"
                  rootError={accountError ?? undefined}
                />
                <Button
                  className="mt-2 w-full"
                  disabled={
                    !isInvoiceNumberValid ||
                    isLookingUp ||
                    result !== null ||
                    addressResults !== null
                  }
                  type="submit"
                >
                  {isLookingUp
                    ? t("quickPay.lookingUp")
                    : t("quickPay.lookUpAccount")}
                </Button>
              </Form>
            )}
          </TabsPanel>

          <TabsPanel value="address">
            <AddressSearchForm
              key={addressFormKey}
              onSearchResults={handleSearchResults}
              tenantId={tenantId}
            />
          </TabsPanel>
        </Tabs>
      </Card.Content>
    </Card>
  );

  if (hasRightPanel) {
    return (
      <div className="my-auto w-full max-w-4xl transition-all duration-300">
        <div className="grid gap-8 md:grid-cols-[minmax(0,1fr)_minmax(0,1.2fr)]">
          {formCard}

          {result && (
            <AccountLookupResults
              onPayNow={() => setStep("pay")}
              result={result}
            />
          )}

          {!result && addressResults && addressResults.length > 0 && (
            <AccountLookupResults
              maskAccount={true}
              onSelect={handleAddressAccountSelected}
              results={addressResults}
            />
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="my-auto w-full max-w-4xl transition-all duration-300">
      <div className="grid items-center gap-12 md:grid-cols-2">
        <div className="flex flex-col gap-6">
          <div>
            <h1 className="font-bold text-3xl tracking-tight">
              {t("quickPay.payYourBillInstantly")}
            </h1>
            <p className="mt-2 text-lg text-muted-foreground">
              {t("quickPay.noAccountRegistration")}
            </p>
          </div>

          <ul className="flex flex-col gap-4">
            <li className="flex items-center gap-3">
              <div className="flex size-9 shrink-0 items-center justify-center rounded-lg bg-primary/10">
                <UserRoundX
                  aria-hidden="true"
                  className="size-5 text-primary"
                />
              </div>
              <div>
                <p className="font-medium text-sm">
                  {t("quickPay.noSignUpRequired")}
                </p>
                <p className="text-muted-foreground text-xs">
                  {t("quickPay.payWithoutAccount")}
                </p>
              </div>
            </li>
            <li className="flex items-center gap-3">
              <div className="flex size-9 shrink-0 items-center justify-center rounded-lg bg-primary/10">
                <Timer aria-hidden="true" className="size-5 text-primary" />
              </div>
              <div>
                <p className="font-medium text-sm">
                  {t("quickPay.instantPayments")}
                </p>
                <p className="text-muted-foreground text-xs">
                  {t("quickPay.paymentProcessedImmediately")}
                </p>
              </div>
            </li>
            <li className="flex items-center gap-3">
              <div className="flex size-9 shrink-0 items-center justify-center rounded-lg bg-primary/10">
                <Lock aria-hidden="true" className="size-5 text-primary" />
              </div>
              <div>
                <p className="font-medium text-sm">
                  {t("quickPay.securePayments")}
                </p>
                <p className="text-muted-foreground text-xs">
                  {t("quickPay.secureVia")}
                </p>
              </div>
            </li>
          </ul>
        </div>

        {formCard}
      </div>
    </div>
  );
}

export default observer(AccountLookup);
